import { useState, type SyntheticEvent } from "react";
import type { Application, ApplicationDraft, ApplicationType } from "../api";
import { Button } from "./ui/Button";
import { Dialog } from "./ui/Dialog";
import { Icon } from "./ui/Icon";

interface Props {
  readonly application?: Application;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (draft: ApplicationDraft) => Promise<void>;
}

export function ApplicationDialog({ application, busy, onClose, onSubmit }: Props) {
  const [appType, setAppType] = useState<ApplicationType>(application?.appType ?? "web");
  const [error, setError] = useState<string | null>(null);

  async function submit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): Promise<void> {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try {
      setError(null);
      await onSubmit({
        name: fieldValue(data, "name").trim(),
        description: fieldValue(data, "description").trim() || null,
        appType,
        redirectUris: lines(fieldValue(data, "redirectUris")),
        allowedOrigins: appType === "web" ? lines(fieldValue(data, "allowedOrigins")) : [],
        adminUserId: fieldValue(data, "adminUserId").trim() || "administrator",
        // 应用级旧回调字段仍属于兼容合同，但不再由应用管理页面编辑。
        // 编辑应用时原样保留已有值，避免保存其他设置时意外清空线上回调。
        callbackUrl: application?.callbackUrl ?? null,
        callbackEvents: application?.callbackEvents ?? [],
        userSigExpireSeconds: Number(fieldValue(data, "userSigExpireSeconds") || 604800),
        push: appType === "native" ? {
          ios: {
            enabled: data.has("iosEnabled"),
            ...(fieldValue(data, "iosBundleId").trim() === "" ? {} : { bundleId: fieldValue(data, "iosBundleId").trim() }),
            environment: "production",
          },
          android: {
            enabled: data.has("androidEnabled"),
            ...(fieldValue(data, "fcmProjectId").trim() === "" ? {} : { projectId: fieldValue(data, "fcmProjectId").trim() }),
          },
        } : {},
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <Dialog
      busy={busy}
      description={appType === "web" ? "创建独立租户与 OIDC public client。" : "创建 AppKey；AppSecret 只在创建完成后展示一次。"}
      eyebrow="IM APPLICATION"
      size="large"
      title={application === undefined ? "创建 IM 应用" : "编辑应用配置"}
      onClose={onClose}
    >
        <form onSubmit={(event) => { void submit(event); }}>
          <div className="field-grid">
            <label className="field field-wide">
              <span>应用名称</span>
              <input name="name" required maxLength={80} defaultValue={application?.name} placeholder="例如：客服工作台" />
            </label>
            <label className="field field-wide">
              <span>应用描述 <em>可选</em></span>
              <textarea name="description" maxLength={500} defaultValue={application?.description ?? ""} placeholder="描述该应用的使用场景" />
            </label>
            <fieldset className="field field-wide type-selector">
              <legend>应用类型 <em>创建后不可修改</em></legend>
              <button type="button" disabled={application !== undefined} className={appType === "web" ? "selected" : ""} onClick={() => { setAppType("web"); }}>
                <span className="type-selector-icon"><Icon name="globe" /></span><span><strong>Web 应用</strong><small>浏览器 / H5，必须配置 Origin</small></span><span className="selection-indicator"><Icon name="check" size={14} /></span>
              </button>
              <button type="button" disabled={application !== undefined} className={appType === "native" ? "selected" : ""} onClick={() => { setAppType("native"); }}>
                <span className="type-selector-icon"><Icon name="smartphone" /></span><span><strong>原生应用</strong><small>iOS / Android / Desktop，使用 AppKey + UserSig</small></span><span className="selection-indicator"><Icon name="check" size={14} /></span>
              </button>
            </fieldset>
            {appType === "web" && <label className="field field-wide">
              <span>Redirect URI <em>每行一个</em></span>
              <textarea name="redirectUris" required defaultValue={application?.redirectUris.join("\n")} placeholder="https://app.example.com/auth/callback" />
              <small>生产 Web 地址必须使用 HTTPS；不允许通配符、账号信息或 URL Fragment。</small>
            </label>}
            {appType === "native" && <>
              <label className="field">
                <span>管理员 userID</span>
                <input name="adminUserId" required maxLength={128} defaultValue={application?.adminUserId ?? "administrator"} />
              </label>
              <label className="field">
                <span>UserSig 有效期（秒）</span>
                <input name="userSigExpireSeconds" type="number" min={60} max={604800} required defaultValue={application?.userSigExpireSeconds ?? 604800} />
              </label>
            </>}
            {appType === "web" && <label className="field field-wide">
              <span>允许的 Origin <em>每行一个</em></span>
              <textarea name="allowedOrigins" required defaultValue={application?.allowedOrigins.join("\n")} placeholder="https://app.example.com" />
              <small>用于 IM API 精确 CORS 校验，只填写 scheme + host + port。</small>
            </label>}
            {appType === "native" && <>
              <fieldset className="field field-wide callback-options">
                <legend>离线推送元数据</legend>
                <label><input type="checkbox" name="iosEnabled" defaultChecked={application?.push.ios?.enabled} /> 登记 APNs 配置（Provider 待上线）</label>
                <input name="iosBundleId" defaultValue={application?.push.ios?.bundleId ?? ""} placeholder="iOS Bundle ID" />
                <label><input type="checkbox" name="androidEnabled" defaultChecked={application?.push.android?.enabled} /> 登记 FCM 配置（Provider 待上线）</label>
                <input name="fcmProjectId" defaultValue={application?.push.android?.projectId ?? ""} placeholder="Firebase Project ID" />
              </fieldset>
            </>}
          </div>
          {error !== null && <p className="form-error" role="alert">{error}</p>}
          <footer className="dialog-actions">
            <Button type="button" onClick={onClose} disabled={busy}>取消</Button>
            <Button loading={busy} type="submit" variant="primary">{application === undefined ? "创建应用" : "保存配置"}</Button>
          </footer>
        </form>
    </Dialog>
  );
}

function lines(value: string): string[] {
  return [...new Set(value.split("\n").map((item) => item.trim()).filter(Boolean))];
}

function fieldValue(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === "string" ? value : "";
}
